import React, {useState} from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import DateTimePicker, {
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';

import {isoDate, parseIsoDate} from '@/entities/attendance/period';
import {Icon} from '@/shared/ui/icons';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppText} from '@/shared/ui/text';
import {controls, radii, useTheme} from '@/shared/ui/theme';

/**
 * A tap-to-open calendar date field. The sibling of TimeField, holding a "YYYY-MM-DD"
 * string — the shape every attendance route takes — rather than a Date, so nothing
 * upstream has to think about time zones.
 *
 * The picker is the platform's own on both targets: Android's calendar dialog, and on
 * web the browser's `<input type="date">` (web/shims/datetimepicker.js). Both refuse
 * an impossible date, which is the reason not to offer a typed DD.MM.YYYY field here —
 * parsing a hand-typed date correctly in two locales is a bug farm, and the report's
 * whole point is that the period is unambiguous.
 *
 * Displayed as DD.MM.YYYY (the ru order the app uses everywhere) and never through
 * `toLocaleDateString`, which silently falls back to US ordering on a Hermes build
 * without ICU data.
 */
const styles = StyleSheet.create({
  field: {gap: 6, flex: 1},
  box: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: controls.lg,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: radii.md,
    borderWidth: 1,
  },
  boxMd: {minHeight: controls.md, paddingVertical: 4},
  value: {flex: 1},
});

/** "2026-08-14" → "14.08.2026". An unparseable value shows the placeholder. */
function display(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '—';
}

export function DateField({
  label,
  value,
  onChange,
  minimumDate,
  maximumDate,
  style,
  testID,
}: {
  label: string;
  /** "YYYY-MM-DD". */
  value: string;
  onChange: (value: string) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const [open, setOpen] = useState(false);

  const handle = (event: DateTimePickerEvent, date?: Date) => {
    setOpen(false);
    if (event.type === 'set' && date) {
      onChange(isoDate(date));
    }
  };

  return (
    <View style={[styles.field, style]}>
      <AppText variant="label">{label}</AppText>
      <Pressable
        testID={testID}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={`${label}: ${display(value)}`}
        style={({pressed}) => [
          styles.box,
          isDesktop && styles.boxMd,
          {backgroundColor: pressed ? p.muted : p.card, borderColor: p.input},
        ]}>
        <Icon name="calendar" size={16} color={p.mutedForeground} />
        <AppText variant={isDesktop ? 'mono' : 'monoLg'} style={styles.value}>
          {display(value)}
        </AppText>
      </Pressable>
      {open ? (
        <DateTimePicker
          value={parseIsoDate(value)}
          mode="date"
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          onChange={handle}
        />
      ) : null}
    </View>
  );
}
